import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  acceptTransfer,
  commitTransfer,
  facetLabels,
  getTransferReceipt,
  listCustodianTransfers,
  rejectTransfer,
  transactionTypeLabel,
} from '../../api/transferApi';
import { formatApiFailure } from '../../api/apiError';
import { ErrorBanner } from '../ui/ErrorBanner';
import { StatusPill } from '../ui/StatusPill';
import { formatDate } from '../../utils/format';

const PRIMARY_BTN =
  'inline-flex items-center justify-center rounded-full bg-evault-teal-deep px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY_BTN =
  'inline-flex items-center justify-center rounded-full border border-evault-border bg-white px-3 py-1.5 text-xs font-semibold text-evault-navy disabled:cursor-not-allowed disabled:opacity-50';
const DANGER_BTN =
  'inline-flex items-center justify-center rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 disabled:cursor-not-allowed disabled:opacity-50';

function displayCell(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

/** Party named by a facet pair, preferring the label over the raw org id. */
function party(facets, prefix) {
  if (!facets) return '—';
  return facets[`${prefix}_label`] || facets[`${prefix}_org_id`] || '—';
}

function counterpartyOf(row) {
  return row.counterparty_label || row.counterparty_org_id || '—';
}

/** Next action the custodian can take for a row, or null when none. */
export function nextActionFor(state) {
  if (state === 'LOCKED') return 'accept';
  if (state === 'ACCEPTED') return 'commit';
  return null;
}

/**
 * Manifest preview: what the committed authority record will say, next to
 * what it says now. The manifest is frozen at lock time, so this is exactly
 * what the custodian is accepting (BRD 9.2, EV-20).
 */
export function ManifestPreview({ transaction }) {
  const manifest = transaction?.manifest;
  if (!manifest) {
    return (
      <p className="text-xs text-evault-muted" data-testid="manifest-missing">
        No manifest yet. The system writes it when the authority version is locked.
      </p>
    );
  }
  const before = manifest.before?.facets || {};
  const after = manifest.after_facets || {};
  const changed = facetLabels(manifest.changed_fields || []);
  const unchanged = facetLabels(manifest.unchanged_fields || []);
  const parties = ['owner', 'control', 'custodian', 'held_for', 'pledgee'];
  return (
    <div className="space-y-3" data-testid="manifest-preview">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <div>
          <dt className="font-semibold text-evault-muted">Locked authority version</dt>
          <dd className="font-mono">{displayCell(manifest.expected_authority_version)}</dd>
        </div>
        <div>
          <dt className="font-semibold text-evault-muted">Manifest hash</dt>
          <dd className="break-all font-mono">{displayCell(transaction.manifest_hash)}</dd>
        </div>
      </dl>
      <table className="w-full border-collapse text-left text-xs">
        <thead>
          <tr className="border-b border-evault-border">
            <th className="py-1.5 pr-3 font-semibold text-evault-muted">Relationship</th>
            <th className="py-1.5 pr-3 font-semibold text-evault-muted">Now</th>
            <th className="py-1.5 pr-3 font-semibold text-evault-muted">After commit</th>
          </tr>
        </thead>
        <tbody>
          {parties.map((prefix) => {
            const was = party(before, prefix);
            const will = party(after, prefix);
            const differs = was !== will;
            return (
              <tr key={prefix} className="border-b border-evault-border/60 last:border-b-0">
                <td className="py-1.5 pr-3 capitalize">{prefix.replace('_', ' ')}</td>
                <td className="py-1.5 pr-3">{was}</td>
                <td className={`py-1.5 pr-3 ${differs ? 'font-semibold text-evault-teal-deep' : ''}`}>
                  {will}
                  {differs ? <span className="sr-only"> (changes)</span> : null}
                </td>
              </tr>
            );
          })}
          <tr>
            <td className="py-1.5 pr-3">Pledged</td>
            <td className="py-1.5 pr-3">{before.pledged ? 'Yes' : 'No'}</td>
            <td className={`py-1.5 pr-3 ${Boolean(before.pledged) !== Boolean(after.pledged) ? 'font-semibold text-evault-teal-deep' : ''}`}>
              {after.pledged ? 'Yes' : 'No'}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-umb-neutral-dark/85">
        <span className="font-semibold">Changes:</span> {changed.length ? changed.join(', ') : 'nothing'}
        {' · '}
        <span className="font-semibold">Stays:</span> {unchanged.length ? unchanged.join(', ') : '—'}
      </p>
      {Array.isArray(manifest.authoritative_files) && manifest.authoritative_files.length ? (
        <p className="text-xs text-evault-muted">
          {manifest.authoritative_files.length} authoritative file(s) stay in the sealed vault record; the
          transfer changes who the record is held for, not the files.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Live custodian queue for transfer transactions.
 * `types` narrows to the given transaction types (e.g. ['PLEDGE','RELEASE']).
 */
export function TransferQueueTable({ title, types, intro, emptyMessage = 'No transfer transactions.' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState('');
  const [openId, setOpenId] = useState('');
  const [stateFilter, setStateFilter] = useState('OPEN');
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const states = stateFilter === 'OPEN' ? ['LOCKED', 'ACCEPTED', 'EXCEPTION'] : stateFilter === 'ALL' ? undefined : [stateFilter];
        const typeList = Array.isArray(types) && types.length ? types : [undefined];
        const pages = await Promise.all(
          typeList.map((transactionType) =>
            listCustodianTransfers({ page: 1, pageSize: 50, states, transactionType })
          )
        );
        if (cancelled) return;
        const merged = pages.flatMap((page) => page?.items || []);
        merged.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
        setRows(merged);
      } catch (requestError) {
        if (!cancelled) {
          setRows([]);
          setError(formatApiFailure(requestError, 'Unable to load transfers.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken, stateFilter, types]);

  async function run(id, fn, label) {
    setBusyId(id);
    setActionError('');
    setReceipt(null);
    try {
      const updated = await fn();
      setRows((current) => current.map((row) => (row.id === id ? { ...row, ...updated } : row)));
      if (label === 'commit' && updated?.state === 'COMPLETED') {
        try {
          setReceipt(await getTransferReceipt(id));
        } catch (receiptError) {
          setActionError(formatApiFailure(receiptError, 'Committed, but the receipt could not be loaded.'));
        }
      }
    } catch (requestError) {
      setActionError(formatApiFailure(requestError, `Unable to ${label} transfer.`));
      setReloadToken((value) => value + 1);
    } finally {
      setBusyId('');
    }
  }

  function reject(id) {
    const reason = typeof window !== 'undefined' && typeof window.prompt === 'function'
      ? window.prompt('Reason for rejecting this transfer (recorded on the transaction):')
      : null;
    if (reason === null) return;
    run(id, () => rejectTransfer(id, reason || null), 'reject');
  }

  return (
    <div className="page-stack">
      <section className="card-shell p-5 sm:p-6">
        <p className="text-sm leading-relaxed text-evault-muted">{intro}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="text-xs font-semibold text-evault-muted" htmlFor="transfer-state-filter">
            Show
          </label>
          <select
            id="transfer-state-filter"
            className="rounded-full border border-evault-select-ring/70 bg-white px-3 py-2 text-xs text-umb-neutral-dark shadow-sm focus:border-evault-teal focus:outline-none focus:ring-2 focus:ring-evault-teal/20 sm:text-sm"
            value={stateFilter}
            onChange={(event) => setStateFilter(event.target.value)}
          >
            <option value="OPEN">Needs action (locked, accepted, exception)</option>
            <option value="ALL">All states</option>
            <option value="LOCKED">Locked</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="EXCEPTION">Exception</option>
          </select>
        </div>
      </section>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}
      {actionError ? (
        <p className="rounded-evault border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {actionError}
        </p>
      ) : null}

      {receipt ? (
        <section className="card-shell p-4" role="status" data-testid="transfer-receipt">
          <p className="text-sm font-semibold text-umb-neutral-dark">
            Transfer committed. Receipt {receipt.receipt_hash_verified ? 'verified' : 'did not verify'}.
          </p>
          <p className="mt-1 break-all font-mono text-xs text-umb-neutral-dark">
            Receipt SHA-256: {receipt.receipt_hash}
          </p>
          <p className="mt-2 text-xs text-evault-muted">
            Authority version {receipt.receipt?.after?.version ?? '—'} is now the record of who holds what.
          </p>
        </section>
      ) : null}

      <div className="table-shell overflow-hidden">
        <div className="border-b border-evault-border bg-evault-surface/80 px-4 py-3">
          <h2 className="text-sm font-semibold text-umb-neutral-dark">{title}</h2>
        </div>
        {error ? null : loading ? (
          <p className="px-4 py-6 text-sm text-evault-muted">Loading transfers…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-evault-muted">{emptyMessage}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse text-left text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-evault-border bg-evault-surface/90">
                  <th className="px-4 py-3 font-semibold text-evault-muted">Asset</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Type</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">From (originator)</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">To (counterparty)</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Locked version</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">State</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Updated</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const next = nextActionFor(row.state);
                  const busy = busyId === row.id;
                  const expanded = openId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr className="border-b border-evault-border last:border-b-0">
                        <td className="px-4 py-3.5">
                          <Link
                            to={`/custodian/assets/${encodeURIComponent(row.package_id)}`}
                            className="font-mono text-[11px] hover:underline sm:text-xs"
                          >
                            {displayCell(row.asset_number || row.asset_id)}
                          </Link>
                          {row.loan_number ? (
                            <div className="text-[11px] text-evault-muted">{row.loan_number}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3.5 font-medium text-evault-teal-deep">
                          {transactionTypeLabel(row.transaction_type)}
                        </td>
                        <td className="px-4 py-3.5 text-umb-neutral-dark/85">{displayCell(row.originator)}</td>
                        <td className="px-4 py-3.5 text-umb-neutral-dark/85">{counterpartyOf(row)}</td>
                        <td className="px-4 py-3.5 font-mono">{displayCell(row.expected_authority_version)}</td>
                        <td className="px-4 py-3.5">
                          <StatusPill value={row.state} />
                          {row.state === 'EXCEPTION' && row.close_reason ? (
                            <div className="mt-1 max-w-[16rem] text-[11px] text-red-700">{row.close_reason}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3.5 text-umb-neutral-dark/80">{formatDate(row.updated_at)}</td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              className={SECONDARY_BTN}
                              onClick={() => setOpenId(expanded ? '' : row.id)}
                              aria-expanded={expanded}
                              aria-controls={`manifest-${row.id}`}
                            >
                              {expanded ? 'Hide manifest' : 'Manifest'}
                            </button>
                            {next === 'accept' ? (
                              <>
                                <button
                                  type="button"
                                  className={PRIMARY_BTN}
                                  disabled={busy}
                                  onClick={() => run(row.id, () => acceptTransfer(row.id, null), 'accept')}
                                >
                                  {busy ? 'Working…' : 'Accept'}
                                </button>
                                <button
                                  type="button"
                                  className={DANGER_BTN}
                                  disabled={busy}
                                  onClick={() => reject(row.id)}
                                >
                                  Reject
                                </button>
                              </>
                            ) : null}
                            {next === 'commit' ? (
                              <button
                                type="button"
                                className={PRIMARY_BTN}
                                disabled={busy}
                                onClick={() => run(row.id, () => commitTransfer(row.id), 'commit')}
                              >
                                {busy ? 'Committing…' : 'Commit'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {expanded ? (
                        <tr className="border-b border-evault-border bg-evault-surface/40 last:border-b-0">
                          <td colSpan={8} className="px-4 py-4" id={`manifest-${row.id}`}>
                            <ManifestPreview transaction={row} />
                            {row.notes ? (
                              <p className="mt-3 text-xs text-umb-neutral-dark/85">
                                <span className="font-semibold">Originator notes:</span> {row.notes}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
